import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  adminGetResultTemplates, adminCreateResultTemplate, 
  adminUpdateResultTemplate, adminDeleteResultTemplate 
} from '@/lib/api';
import { toast } from 'sonner';

const AdminResultTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    template_name: '',
    support_area_label: '',
    explanation_template: '',
    disclaimer_text: 'These recommendations are for educational purposes only and are not medical advice. Please consult your healthcare practitioner before beginning any nutritional program.'
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const data = await adminGetResultTemplates();
      setTemplates(data || []);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingTemplate(null);
    setFormData({
      template_name: '',
      support_area_label: '',
      explanation_template: '',
      disclaimer_text: 'These recommendations are for educational purposes only and are not medical advice. Please consult your healthcare practitioner before beginning any nutritional program.'
    });
    setDialogOpen(true);
  };

  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setFormData({
      template_name: template.template_name,
      support_area_label: template.support_area_label,
      explanation_template: template.explanation_template,
      disclaimer_text: template.disclaimer_text
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTemplate) {
        await adminUpdateResultTemplate(editingTemplate.id, formData);
        toast.success('Template updated');
      } else {
        await adminCreateResultTemplate(formData);
        toast.success('Template created');
      }
      setDialogOpen(false);
      loadTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Delete "${template.template_name}"?`)) return;
    try {
      await adminDeleteResultTemplate(template.id);
      toast.success('Template deleted');
      loadTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  if (loading) {
    return <div className="text-stone-400 p-8">Loading templates...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl text-[#2C2C2C] mb-1">Result Templates</h1>
          <p className="text-stone-600 text-sm">Customize explanations for quiz results</p>
        </div>
        <Button onClick={openCreateDialog} className="btn-primary" data-testid="add-template-btn">
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Add Template
        </Button>
      </div>

      {templates.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <p className="text-stone-500 mb-4">No result templates created yet</p>
            <Button onClick={openCreateDialog} variant="outline" className="btn-outline">
              Create Your First Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {templates.map((template) => (
            <Card key={template.id} className="card-hover" data-testid={`template-card-${template.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-medium text-[#2C2C2C]">{template.template_name}</h3>
                    <p className="text-sm text-[#4A6763]">{template.support_area_label}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(template)}
                      className="h-8 w-8 text-stone-400 hover:text-[#4A6763]"
                    >
                      <Pencil className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(template)}
                      className="h-8 w-8 text-stone-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">Explanation</p>
                    <p className="text-sm text-stone-600 line-clamp-3">{template.explanation_template}</p>
                  </div>
                  <div>
                    <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">Disclaimer</p>
                    <p className="text-xs text-stone-500 italic line-clamp-2">{template.disclaimer_text}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editingTemplate ? 'Edit Template' : 'Add Template'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <label className="form-label">Template Name</label>
                <Input
                  value={formData.template_name}
                  onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                  placeholder="e.g., Digestive Support"
                  required
                  data-testid="template-name-input"
                />
              </div>
              <div>
                <label className="form-label">Support Area Label</label>
                <Input
                  value={formData.support_area_label}
                  onChange={(e) => setFormData({ ...formData, support_area_label: e.target.value })}
                  placeholder="e.g., Digestive Health"
                  required
                  data-testid="template-label-input"
                />
              </div>
              <div>
                <label className="form-label">Explanation Text</label>
                <Textarea
                  value={formData.explanation_template}
                  onChange={(e) => setFormData({ ...formData, explanation_template: e.target.value })}
                  placeholder="Based on your responses, you may benefit from..."
                  rows={4}
                  required
                  data-testid="template-explanation-input"
                />
              </div>
              <div>
                <label className="form-label">Disclaimer Text</label>
                <Textarea
                  value={formData.disclaimer_text}
                  onChange={(e) => setFormData({ ...formData, disclaimer_text: e.target.value })}
                  rows={3}
                  required
                  data-testid="template-disclaimer-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="save-template-btn">
                {editingTemplate ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminResultTemplates;
